import { validateUrl } from '#lib/urlValidation';

import { derivePalette, readableTextColor } from '../utils/pmColor';

import { pdfSheets } from './pdfSheets';

// The PIN eBrief A4 print spec (margins, address-window geometry, fold marks,
// Textbeginn) lives entirely in CSS custom properties on .pm-letter
// (--pm-layout-*) in styles/postmailing.scss — the single source of truth for
// layout. The safe-zone overlay reads those same vars, so there is no JS-side
// copy of the measurements here.

// Default accent colour for new/partial pages. The full palette (primaryDark,
// onPrimary, soft) is derived from a chosen primary via derivePalette().
export const PM_ACCENT_DEFAULT = '#5b5bf5';

// The letter header is a fixed logo slot (page.logo), not a free block zone.
// `show` reserves the header height even without a logo so the PIN spacing
// (111mm text start) stays stable; `align` positions the logo full-width;
// `widthMm` is the rendered width in mm (height scales proportionally, bounded
// by the header height in CSS).
export const PM_LOGO_DEFAULT_WIDTH_MM = 45;
export const PM_LOGO_MIN_WIDTH_MM = 20;
export const PM_LOGO_MAX_WIDTH_MM = 80;

// Default logo source: the org-scoped merge token, not an empty string. This
// wires the logo slot into the same {{org.*}} resolution path every other org
// field already uses — useResolvedDesign fills it in the editor preview and the
// worker's applyMergeTokens fills it at PDF/send time. So a new letter shows the
// org's logo from the company profile automatically; uploading a custom logo
// overwrites the token, and removing it falls back to the token again.
export const PM_ORG_LOGO_TOKEN = '{{org.logoUrl}}';
export const PM_LOGO_DEFAULT = { src: PM_ORG_LOGO_TOKEN, align: 'left', show: true, widthMm: PM_LOGO_DEFAULT_WIDTH_MM };

/**
 * Resolves the page's logo slot to a complete object, applying defaults for any
 * missing field.
 *
 * @param {Object} [page] - Editor page state.
 * @param {{ src?: string, align?: 'left'|'center'|'right', show?: boolean, widthMm?: number }} [page.logo]
 * @returns {{ src: string, align: 'left'|'center'|'right', show: boolean, widthMm: number }}
 */
export const pmLogo = (page) => ({ ...PM_LOGO_DEFAULT, ...(page?.logo ?? {}) });

export const PM_FONTS = {
	inter: { stack: "'Inter', system-ui, sans-serif", label: 'Inter — Modern' },
	serif: { stack: "'Source Serif 4', Georgia, serif", label: 'Source Serif — Klassisch' },
	ibm: { stack: "'IBM Plex Sans', sans-serif", label: 'IBM Plex — Solide' },
	lora: { stack: "'Lora', Georgia, serif", label: 'Lora — Warm' },
};

// Page-wide text size: a single factor applied to the .pm-letter root font-size
// (--pm-text-scale), so every block scales proportionally and the typographic
// hierarchy (heading > body > fine print) is preserved. theme.textSize stores the key.
export const PM_TEXT_SIZES = {
	small: { factor: 0.85, label: 'Klein' },
	medium: { factor: 1, label: 'Mittel' },
	large: { factor: 1.15, label: 'Groß' },
};
export const PM_TEXT_SIZE_DEFAULT = 'medium';

/**
 * Resolves a page's text-size key to its scale factor, falling back to the default.
 * @param {object} [page] - Editor page state (reads page.theme.textSize).
 * @returns {number} The font-size scale factor for --pm-text-scale.
 */
export const pmTextScale = (page) =>
	(PM_TEXT_SIZES[page?.theme?.textSize] ?? PM_TEXT_SIZES[PM_TEXT_SIZE_DEFAULT]).factor;

/**
 * Whether a design has any printable letter content: a PDF template needs at least one uploaded
 * front page (pdfSheets normalizes both the sheets[] and the legacy flat frontAssetId shape), a
 * block design at least one block in the body zone (sender/address are structural and exist on
 * every design). Guards the send flow against paying for an empty letter.
 * @param {object} [designData]
 * @returns {boolean}
 */
export const designHasContent = (designData) => {
	if (!designData || Object.keys(designData).length === 0) return false;
	if (designData.kind === 'pdf') {
		return pdfSheets(designData).some((sheet) => Boolean(sheet.frontAssetId));
	}
	return (designData.zones?.body?.length ?? 0) > 0;
};

const needsQr = (block) => block.type === 'qrCode' || (block.type === 'callToAction' && Boolean(block.showQr));

/**
 * Whether a QR-bearing block (standalone QR, or CTA banner with QR, also inside containers) lacks a
 * valid http(s) target. Walks the zones like the backend's collectQrBlocks; an empty link counts
 * as invalid because the scan redirect rejects it.
 *
 * @param {Object} designData
 * @returns {boolean}
 */
export const designHasInvalidQrLink = (designData) => {
	const walk = (value) => {
		if (Array.isArray(value)) return value.some(walk);
		if (!value || typeof value !== 'object') return false;
		if (needsQr(value) && !validateUrl(value.qrUrl).normalized) return true;
		return Object.values(value).some(walk);
	};
	return walk(designData?.zones);
};

export const PM_ZONE_LABEL = {
	sender: 'Absenderzeile',
	address: 'Adressfenster',
	body: 'Hauptteil',
	sidebar: 'Seitenleiste',
	footer: 'Fußzeile',
};

// Zones whose block list is fixed: no blocks can be added and the existing
// block(s) cannot be removed (editing the text inline stays allowed). The
// sender line and the recipient address window are single, mandatory,
// format-constrained elements — letting users stack arbitrary blocks there or
// delete them breaks the DIN letter layout and the address-window position.
export const PM_LOCKED_ZONES = new Set(['sender', 'address']);

// Default sidebar colours when a page has not set its own. The gradient runs
// from PM_SIDEBAR_FROM (top) to PM_SIDEBAR_TO (bottom) with PM_SIDEBAR_TEXT
// foreground. Shared by LetterCanvas, LetterSettingsPanel and the page templates so the
// editor, the picker defaults and new pages always start in sync.
export const PM_SIDEBAR_FROM = '#4b4ce1';
export const PM_SIDEBAR_TO = '#15163e';
export const PM_SIDEBAR_TEXT = '#ffffff';

// Sidebar column width (theme.sidebarWidth). Designs saved before the option existed carry no key
// and keep the original 60mm so their printed layout never shifts; new designs start on `medium`.
export const PM_SIDEBAR_WIDTHS = {
	narrow: { mm: 48, label: 'Schmal' },
	medium: { mm: 54, label: 'Mittel' },
	wide: { mm: 60, label: 'Breit' },
};
export const PM_SIDEBAR_WIDTH_LEGACY = 'wide';
export const PM_SIDEBAR_WIDTH_NEW = 'medium';

// Sidebar fill style (theme.sidebarStyle): `bleed` runs the fill to the paper edge and page bottom,
// `card` ends it with the content inside the type margins, `plain` drops the fill entirely.
export const PM_SIDEBAR_STYLES = {
	bleed: { label: 'Vollflächig' },
	card: { label: 'Karte' },
	plain: { label: 'Ohne' },
};
export const PM_SIDEBAR_STYLE_DEFAULT = 'bleed';
const PM_SIDEBAR_PLAIN_TEXT = '#111111';

// Page-wide corner style (theme.corners): one factor on every block radius (--pm-radius-scale).
export const PM_CORNER_STYLES = {
	square: { factor: 0, label: 'Eckig' },
	soft: { factor: 1, label: 'Weich' },
	round: { factor: 2, label: 'Rund' },
};
export const PM_CORNER_STYLE_DEFAULT = 'soft';

/**
 * Derives the structural render flags for a page from its independent settings.
 * Single source of truth shared by LetterCanvas, LetterStructureTree and LetterThumbnail so the three
 * render paths can never diverge.
 *
 * Both the sidebar and the footer can be filled with a solid colour or a linear
 * gradient (from → to at a chosen angle), selectable via theme.*ColorMode. In
 * solid mode the `*From` colour is the fill. `sidebarBackground` / `footerBackground`
 * resolve that choice into one CSS background value so the canvas sets a single
 * custom property each. The footer defaults to solid on `footerBg` (which itself
 * defaults to the sidebar's end colour) so existing templates look unchanged.
 * `*Text` defaults to a legible foreground derived from the dominant fill colour.
 * The sidebar always stays within the content area (it does not run over the footer).
 *
 * @param {Object} page - Editor page state.
 * @param {'simple'|'sidebar'|'coloredSidebar'} [page.layout] - Structure: no sidebar, light sidebar, or coloured sidebar.
 * @param {boolean} [page.hasFooter] - Whether the footer zone is shown.
 * @param {Object} [page.theme] - Sidebar/footer fill colours, modes, angles and text colours.
 * @returns {Object} Resolved render flags incl. sidebarBackground / footerBackground CSS values.
 */
export const pmPageFlags = (page) => {
	const t = page?.theme ?? {};

	// Sidebar fill: solid colour (= sidebarFrom) or gradient (from → to @ angle).
	const sidebarFrom = t.sidebarFrom ?? PM_SIDEBAR_FROM;
	const sidebarTo = t.sidebarTo ?? PM_SIDEBAR_TO;
	const sidebarColorMode = t.sidebarColorMode ?? 'gradient';
	const sidebarAngle = t.sidebarAngle ?? 180;
	const sidebarStyle = PM_SIDEBAR_STYLES[t.sidebarStyle] ? t.sidebarStyle : PM_SIDEBAR_STYLE_DEFAULT;
	const sidebarFill =
		sidebarColorMode === 'solid'
			? sidebarFrom
			: `linear-gradient(${sidebarAngle}deg, ${sidebarFrom} 0%, ${sidebarTo} 100%)`;
	const sidebarBackground = sidebarStyle === 'plain' ? 'transparent' : sidebarFill;

	// Footer fill: defaults to solid on footerBg (= sidebarTo) for backward
	// compatibility; can be switched to a gradient with its own from/to/angle.
	const footerBg = t.footerBg ?? sidebarTo;
	const footerColorMode = t.footerColorMode ?? 'solid';
	const footerFrom = t.footerFrom ?? footerBg;
	const footerTo = t.footerTo ?? derivePalette(footerFrom).primaryDark;
	const footerAngle = t.footerAngle ?? 180;
	const footerBackground =
		footerColorMode === 'gradient'
			? `linear-gradient(${footerAngle}deg, ${footerFrom} 0%, ${footerTo} 100%)`
			: footerFrom;

	// Top band fill: defaults to the accent gradient (primary → primaryDark @ 90deg) so it looks
	// like before when no custom colours are set; switchable to solid or a custom gradient.
	const accentPalette = derivePalette(t.primary ?? PM_ACCENT_DEFAULT);
	const topBarFrom = t.topBarFrom ?? t.primary ?? PM_ACCENT_DEFAULT;
	const topBarTo = t.topBarTo ?? t.primaryDark ?? accentPalette.primaryDark;
	const topBarColorMode = t.topBarColorMode ?? 'gradient';
	const topBarAngle = t.topBarAngle ?? 90;
	const topBarBackground =
		topBarColorMode === 'solid'
			? topBarFrom
			: `linear-gradient(${topBarAngle}deg, ${topBarFrom} 0%, ${topBarTo} 100%)`;

	return {
		hasSidebar: page?.layout === 'sidebar' || page?.layout === 'coloredSidebar',
		hasFooter: Boolean(page?.hasFooter),
		topBarFrom,
		topBarTo,
		topBarColorMode,
		topBarAngle,
		topBarBackground,
		topBarText: t.topBarText ?? t.onPrimary ?? readableTextColor(topBarFrom),
		sidebarFrom,
		sidebarTo,
		sidebarText: sidebarStyle === 'plain' ? PM_SIDEBAR_PLAIN_TEXT : (t.sidebarText ?? PM_SIDEBAR_TEXT),
		sidebarStyle,
		sidebarWidthMm: (PM_SIDEBAR_WIDTHS[t.sidebarWidth] ?? PM_SIDEBAR_WIDTHS[PM_SIDEBAR_WIDTH_LEGACY]).mm,
		radiusScale: (PM_CORNER_STYLES[t.corners] ?? PM_CORNER_STYLES[PM_CORNER_STYLE_DEFAULT]).factor,
		sidebarColorMode,
		sidebarAngle,
		sidebarBackground,
		footerFrom,
		footerTo,
		footerColorMode,
		footerAngle,
		footerBackground,
		footerText: t.footerText ?? readableTextColor(footerFrom),
	};
};
